import { useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Search,
} from "lucide-react";
import { patientQueryKeys, patientsApi } from "../../shared/api/patients";
import { useDebounce } from "../../shared/hooks/useDebounce";
import { param, useUrlFilters } from "../../shared/hooks/useUrlFilters";
import type {
  PatientGender,
  PatientListFilter,
  PatientStatus,
  PatientSummaryResponse,
} from "../../shared/types/patient";
import {
  formatDate,
  formatPhone,
  genderLabel,
  statusLabel,
} from "../../shared/format";
import { apiError } from "../../shared/api/client";
import {
  downloadImportResult,
  downloadPatientsXlsx,
  downloadTemplate,
  parseWorkbook,
  patientImportApi,
  type ExportRow,
  type ImportResult,
  type ImportRow,
} from "../../shared/api/patientImport";
import {
  BulkDeleteBar,
  runBulkDelete,
  useRowSelection,
} from "../../shared/ui/bulkSelect";
import { useGeoNames } from "../../shared/api/geo";
import { PatientAvatar } from "./components/PatientAvatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge, type BadgeProps } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const PAGE_SIZE = 20;

type BadgeVariant = NonNullable<BadgeProps["variant"]>;

const STATUS_VARIANT: Record<string, BadgeVariant> = {
  ACTIVE: "success",
  INACTIVE: "muted",
  MERGED: "secondary",
  DECEASED: "destructive",
};

function statusVariant(status: PatientStatus): BadgeVariant {
  return STATUS_VARIANT[status] ?? "muted";
}

export function PatientListPage() {
  // Bộ lọc nằm trên URL: tới từ ô tìm kiếm trên topbar (`?q=`) hay một link gửi đi là mở đúng
  // kết quả, và reload không mất bộ lọc. Vắng mặt = không lọc.
  const { filters, set } = useUrlFilters({
    q: param.string(),
    status: param.oneOf(Object.keys(statusLabel) as PatientStatus[]),
    gender: param.oneOf(Object.keys(genderLabel) as PatientGender[]),
    page: param.int(),
  });
  const { q, status, gender, page } = filters;
  const debouncedQ = useDebounce(q.trim(), 300);

  const filter: PatientListFilter = useMemo(
    () => ({
      q: debouncedQ || null,
      status: status || null,
      gender: gender || null,
    }),
    [debouncedQ, status, gender]
  );

  const query = useQuery({
    queryKey: patientQueryKeys.list(filter, page),
    queryFn: () =>
      patientsApi.list(filter, { page, size: PAGE_SIZE, sort: "updatedAt,desc" }),
    placeholderData: (prev) => prev,
  });

  const totalPages = query.data?.totalPages ?? 0;
  const totalElements = query.data?.totalElements ?? 0;

  const queryClient = useQueryClient();
  const { provinceName, wardName } = useGeoNames();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState<null | "export" | "import">(null);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  // #18541: keep the parsed rows so the result file can echo the original data.
  const [importRows, setImportRows] = useState<ImportRow[] | null>(null);
  const [ioError, setIoError] = useState<string | null>(null);

  const sel = useRowSelection();
  const [bulkPending, setBulkPending] = useState(false);

  const rows = query.data?.content ?? [];
  const allSelected = rows.length > 0 && rows.every((p) => sel.selectedIds.has(p.id));
  const someSelected = rows.some((p) => sel.selectedIds.has(p.id)) && !allSelected;
  function toggleAll() {
    const next = new Set(sel.selectedIds);
    if (allSelected) rows.forEach((p) => next.delete(p.id));
    else rows.forEach((p) => next.add(p.id));
    sel.setSelectedIds(next);
  }

  const DELETE_WARNING =
    "Xoá bệnh nhân sẽ xoá cả lịch hẹn và hồ sơ khám. Không thể hoàn tác.";

  async function onDeleteRow(id: string) {
    if (!confirm(DELETE_WARNING)) return;
    try {
      await patientsApi.remove(id);
      queryClient.invalidateQueries({ queryKey: patientQueryKeys.all });
    } catch (e) {
      setIoError(apiError(e).detail);
    }
  }

  /** Export every patient matching the current filter to .xlsx (paginates the list API). */
  async function onExport() {
    setBusy("export");
    setIoError(null);
    try {
      const rows: ExportRow[] = [];
      const SIZE = 500;
      let p = 0;
      for (;;) {
        const pageData = await patientsApi.list(filter, { page: p, size: SIZE, sort: "fullName,asc" });
        for (const x of pageData.content) {
          // 8 columns: Họ&Tên, Tên thân nhân, Email, Số ĐT, Ngày sinh, Địa chỉ, Phường/Xã, Tỉnh.
          // guardianName + wardCode now travel on the list summary; ward/province
          // codes resolve to names via the geo maps (blank when the patient has none).
          const province = x.provinceCode
            ? provinceName.get(String(x.provinceCode)) ?? ""
            : "";
          const ward = x.wardCode ? wardName.get(String(x.wardCode)) ?? "" : "";
          rows.push([
            x.fullName,
            x.guardianName ?? "",
            x.email ?? "",
            x.phoneE164 ?? "",
            x.dateOfBirth ?? "",
            x.addressLine ?? "",
            ward,
            province,
          ]);
        }
        if (p + 1 >= (pageData.totalPages ?? 1) || pageData.content.length === 0) break;
        p += 1;
      }
      downloadPatientsXlsx(rows);
    } catch (e) {
      setIoError(apiError(e).detail);
    } finally {
      setBusy(null);
    }
  }

  async function onImportFile(file: File) {
    setBusy("import");
    setIoError(null);
    setImportResult(null);
    setImportRows(null);
    try {
      const parsed = await parseWorkbook(file);
      if (!parsed || parsed.length === 0) {
        setIoError('Không đọc được file. Cần dòng tiêu đề có cột "Họ & Tên".');
        return;
      }
      const res = await patientImportApi.import(parsed);
      setImportRows(parsed);
      setImportResult(res);
      await queryClient.invalidateQueries({ queryKey: patientQueryKeys.all });
    } catch (e) {
      setIoError(apiError(e).detail);
    } finally {
      setBusy(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-1">
          <h1 className="text-2xl font-bold tracking-tight text-foreground">Bệnh nhân</h1>
          <p className="text-sm text-muted-foreground">
            Tìm kiếm, lọc và quản lý hồ sơ bệnh nhân của phòng khám.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button type="button" variant="secondary" onClick={downloadTemplate}>
            Tải mẫu Excel
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={onExport}
            disabled={busy !== null}
          >
            {busy === "export" ? "Đang xuất…" : "Xuất Excel"}
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={() => fileInputRef.current?.click()}
            disabled={busy !== null}
          >
            {busy === "import" ? "Đang nhập…" : "Nhập Excel"}
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xls,.xlsx,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) onImportFile(f);
            }}
          />
          <Button asChild variant="secondary">
            <Link to="/patients/merge">Hồ sơ trùng</Link>
          </Button>
          <Button asChild>
            <Link to="/patients/new">+ Tạo bệnh nhân</Link>
          </Button>
        </div>
      </div>

      {ioError && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {ioError}
        </div>
      )}
      {importResult && (
        <div className="grid gap-1.5 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
          <span>
            Đã nhập <strong>{importResult.created}</strong>/{importResult.total} bệnh nhân
            {importResult.errors.length > 0 ? `, ${importResult.errors.length} dòng lỗi.` : "."}
          </span>
          {importResult.errors.length > 0 && (
            <ul className="m-0 list-disc pl-[18px] text-[13px]">
              {importResult.errors.slice(0, 8).map((e) => (
                <li key={e.index}>
                  Dòng {e.index + 2}
                  {e.fullName ? ` (${e.fullName})` : ""}: {e.error}
                </li>
              ))}
              {importResult.errors.length > 8 && (
                <li>…và {importResult.errors.length - 8} dòng khác</li>
              )}
            </ul>
          )}
          {importRows && (
            <div>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => downloadImportResult(importRows, importResult)}
              >
                Tải kết quả import
              </Button>
            </div>
          )}
        </div>
      )}

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            type="search"
            className="pl-9"
            placeholder="Tìm theo tên (có/không dấu), số ĐT, mã BN, CCCD…"
            value={q}
            onChange={(e) => set({ q: e.target.value })}
            autoFocus
          />
        </div>
        <div className="flex gap-2">
          <select
            value={status}
            onChange={(e) => set({ status: e.target.value as PatientStatus | "" })}
            aria-label="Lọc theo trạng thái"
            className="h-9 rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
          >
            <option value="">Tất cả trạng thái</option>
            {(Object.keys(statusLabel) as PatientStatus[]).map((s) => (
              <option key={s} value={s}>
                {statusLabel[s]}
              </option>
            ))}
          </select>
          <select
            value={gender}
            onChange={(e) => set({ gender: e.target.value as PatientGender | "" })}
            aria-label="Lọc theo giới tính"
            className="h-9 rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
          >
            <option value="">Mọi giới tính</option>
            {(Object.keys(genderLabel) as PatientGender[]).map((g) => (
              <option key={g} value={g}>
                {genderLabel[g]}
              </option>
            ))}
          </select>
        </div>
      </div>

      <BulkDeleteBar
        count={sel.count}
        noun="bệnh nhân"
        pending={bulkPending}
        onClear={sel.clear}
        onDelete={async () => {
          setBulkPending(true);
          setIoError(null);
          const { failed } = await runBulkDelete(
            [...sel.selectedIds],
            patientsApi.remove
          );
          sel.clear();
          queryClient.invalidateQueries({ queryKey: patientQueryKeys.all });
          if (failed.length) setIoError(`${failed.length} mục không xoá được.`);
          setBulkPending(false);
        }}
      />

      <div className="rounded-md border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-9 text-center">
                <input
                  type="checkbox"
                  aria-label="Chọn tất cả"
                  className="h-4 w-4 rounded border-input accent-primary align-middle"
                  checked={allSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = someSelected;
                  }}
                  onChange={toggleAll}
                />
              </TableHead>
              <TableHead>Mã BN</TableHead>
              <TableHead>Họ &amp; Tên</TableHead>
              <TableHead>Giới tính</TableHead>
              <TableHead>Ngày sinh</TableHead>
              <TableHead>Tuổi</TableHead>
              <TableHead>Số ĐT</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Địa chỉ</TableHead>
              <TableHead>Tỉnh/TP</TableHead>
              <TableHead>Trạng thái</TableHead>
              <TableHead>Cập nhật</TableHead>
              <TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {query.isLoading && (
              <TableRow>
                <TableCell colSpan={13} className="py-8 text-center text-muted-foreground">
                  Đang tải…
                </TableCell>
              </TableRow>
            )}
            {query.isError && (
              <TableRow>
                <TableCell colSpan={13} className="py-8 text-center text-destructive">
                  <span className="inline-flex items-center gap-1.5">
                    <AlertTriangle className="h-4 w-4" aria-hidden="true" />
                    {apiError(query.error).detail}
                  </span>
                </TableCell>
              </TableRow>
            )}
            {!query.isLoading && (query.data?.content.length ?? 0) === 0 && (
              <TableRow>
                <TableCell colSpan={13} className="py-8 text-center text-muted-foreground">
                  Không tìm thấy bệnh nhân nào khớp.
                </TableCell>
              </TableRow>
            )}
            {query.data?.content.map((p) => (
              <PatientRow
                key={p.id}
                p={p}
                provinceLabel={
                  p.provinceCode
                    ? provinceName.get(String(p.provinceCode)) ?? p.provinceCode
                    : "—"
                }
                selected={sel.selectedIds.has(p.id)}
                onToggle={() => sel.toggle(p.id)}
                onDelete={() => onDeleteRow(p.id)}
              />
            ))}
          </TableBody>
        </Table>
        {totalPages > 0 && (
          <Pagination
            page={page}
            totalPages={totalPages}
            totalElements={totalElements}
            pageSize={PAGE_SIZE}
            onChange={(p) => set({ page: p })}
          />
        )}
      </div>
    </div>
  );
}

function PatientRow({
  p,
  provinceLabel,
  selected,
  onToggle,
  onDelete,
}: {
  p: PatientSummaryResponse;
  provinceLabel: string;
  selected: boolean;
  onToggle: () => void;
  onDelete: () => void;
}) {
  return (
    <TableRow
      className="cursor-pointer"
      onClick={() => {
        window.location.assign(`/patients/${p.id}`);
      }}
    >
      <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
        <input
          type="checkbox"
          aria-label="Chọn dòng"
          className="h-4 w-4 rounded border-input accent-primary align-middle"
          checked={selected}
          onChange={onToggle}
        />
      </TableCell>
      <TableCell className="font-mono text-xs">{p.patientCode ?? "—"}</TableCell>
      <TableCell>
        <div className="flex items-center gap-2">
          <PatientAvatar
            fullName={p.fullName}
            zaloAvatarUrl={p.zaloAvatarUrl}
            avatarUrl={p.avatarUrl}
            className="size-8 text-xs"
          />
          <strong className="font-semibold text-foreground">{p.fullName}</strong>
        </div>
      </TableCell>
      <TableCell>{p.gender ? genderLabel[p.gender] : "—"}</TableCell>
      <TableCell>{formatDate(p.dateOfBirth)}</TableCell>
      <TableCell>{p.age ?? "—"}</TableCell>
      <TableCell>{formatPhone(p.phoneE164)}</TableCell>
      <TableCell>{p.email ?? "—"}</TableCell>
      <TableCell>{p.addressLine ?? "—"}</TableCell>
      <TableCell>{provinceLabel}</TableCell>
      <TableCell>
        <Badge variant={statusVariant(p.status)}>{statusLabel[p.status]}</Badge>
      </TableCell>
      <TableCell className="font-mono text-xs text-muted-foreground">
        {formatDate(p.updatedAt)}
      </TableCell>
      <TableCell onClick={(e) => e.stopPropagation()}>
        <Button
          type="button"
          variant="destructive"
          size="sm"
          onClick={onDelete}
        >
          Xoá
        </Button>
      </TableCell>
    </TableRow>
  );
}

function Pagination({
  page,
  totalPages,
  totalElements,
  pageSize,
  onChange,
}: {
  page: number;
  totalPages: number;
  totalElements: number;
  pageSize: number;
  onChange: (p: number) => void;
}) {
  const start = page * pageSize + 1;
  const end = Math.min((page + 1) * pageSize, totalElements);
  return (
    <div className="flex flex-col gap-3 border-t px-3 py-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="text-sm text-muted-foreground">
        Hiển thị {start}–{end} trong {totalElements} bệnh nhân
      </div>
      <div className="flex items-center gap-1">
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-8 w-8"
          aria-label="Trang đầu"
          disabled={page === 0}
          onClick={() => onChange(0)}
        >
          <ChevronsLeft className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-8 w-8"
          aria-label="Trang trước"
          disabled={page === 0}
          onClick={() => onChange(page - 1)}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <span className="px-2 text-sm font-medium tabular-nums text-foreground">
          {page + 1} / {totalPages}
        </span>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-8 w-8"
          aria-label="Trang sau"
          disabled={page >= totalPages - 1}
          onClick={() => onChange(page + 1)}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-8 w-8"
          aria-label="Trang cuối"
          disabled={page >= totalPages - 1}
          onClick={() => onChange(totalPages - 1)}
        >
          <ChevronsRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
